import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { ChevronDownSmallIcon } from '@/assets/icons/icons';
import TeamTagPill from './TeamsTagPill/TeamTagPill';
import EllipsisTooltip from './EllipsisTooltip';
import SearchInput from './SearchInput';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import './style/TeamDropdownSelect.css';

export interface TeamDropdownSelectTeam {
  id: string;
  name: string;
  color?: string;
  permissions?: {
    is_create?: boolean;
    is_update?: boolean;
  };
  disabled?: boolean;
}

interface TeamDropdownSelectProps {
  teamsOptions: TeamDropdownSelectTeam[];
  selectedTeams: TeamDropdownSelectTeam[];
  onChange: (teams: TeamDropdownSelectTeam[]) => void;
  singleSelect?: boolean;
  formState?: string;
  hideChevronWhenDisabled?: boolean;
  disabled?: boolean;
  onSearch?: (value: string) => void;
  teamsSearch?: string;
  showSearch?: boolean;
  label?: React.ReactNode;
  className?: string;
}

const TeamDropdownSelect: React.FC<TeamDropdownSelectProps> = ({
  teamsOptions,
  selectedTeams,
  onChange,
  singleSelect = false,
  formState = '',
  hideChevronWhenDisabled = false,
  disabled = false,
  onSearch,
  teamsSearch = '',
  showSearch,
  label,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>();
  const searchValue = onSearch ? teamsSearch : localSearch;
  // Always show search by default (same as product dropdown). Callers can
  // still opt out with showSearch={false}.
  const shouldShowSearch = showSearch ?? true;

  const updateSearch = useCallback(
    (value: string) => {
      if (onSearch) {
        onSearch(value);
      } else {
        setLocalSearch(value);
      }
    },
    [onSearch]
  );

  const clearSearch = useCallback(() => {
    updateSearch('');
  }, [updateSearch]);

  const setDropdownOpen = useCallback(
    (visible: boolean) => {
      if (disabled) return;
      setOpen(visible);
      if (visible && triggerRef.current) {
        setDropdownWidth(triggerRef.current.clientWidth);
      }
      if (!visible && searchValue) {
        clearSearch();
      }
    },
    [clearSearch, disabled, searchValue]
  );

  const toggleOpen = useCallback(() => {
    setDropdownOpen(!open);
  }, [open, setDropdownOpen]);

  const availableTeams = useMemo(
    () =>
      onSearch
        ? teamsOptions
        : teamsOptions.filter((team) =>
            team.name.toLowerCase().includes(searchValue.trim().toLowerCase())
          ),
    [onSearch, searchValue, teamsOptions]
  );

  const isTeamDisabled = (team: TeamDropdownSelectTeam) => {
    const hasPermission =
      formState === 'add'
        ? team?.permissions?.is_create
        : formState === 'edit'
          ? team?.permissions?.is_update
          : true;

    return team.disabled || !hasPermission;
  };

  const handleSelect = (team: TeamDropdownSelectTeam) => {
    if (isTeamDisabled(team)) return;
    const isSelected = selectedTeams.some(
      (selected) => selected.id === team.id
    );

    if (isSelected) {
      onChange(selectedTeams.filter((selected) => selected.id !== team.id));
      return;
    }

    onChange(singleSelect ? [team] : [...selectedTeams, team]);
    if (singleSelect) {
      setDropdownOpen(false);
    }
  };

  const handleRemove = (teamId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (disabled) return;
    onChange(selectedTeams.filter((team) => team.id !== teamId));
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  const menu = (
    <div
      className='team-dropdown-select-menu'
      style={{ width: dropdownWidth ? `${dropdownWidth}px` : '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      {shouldShowSearch && (
        <>
          <div className='team-dropdown-select-search-row'>
            <SearchInput
              placeholder={`Search ${themeT('team', theme.themeType).toLowerCase()}`}
              onChange={updateSearch}
              onClear={clearSearch}
              defaultValue={searchValue}
              width='100%'
            />
          </div>
          <div className='team-dropdown-select-divider' />
        </>
      )}

      <div className='team-dropdown-select-options'>
        {availableTeams.length > 0 ? (
          availableTeams.map((team) => {
            const isSelected = selectedTeams.some(
              (selected) => selected.id === team.id
            );
            const isDisabled = isTeamDisabled(team);

            return (
              <button
                type='button'
                key={team.id}
                className={`team-dropdown-select-option ${isSelected ? 'is-selected' : ''} ${
                  isDisabled ? 'is-disabled' : ''
                }`.trim()}
                onClick={() => handleSelect(team)}
                disabled={isDisabled}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={isDisabled}
                  className='team-dropdown-select-checkbox checkbox-sm'
                  onChange={() => handleSelect(team)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span
                  className='team-dropdown-select-dot'
                  style={{
                    backgroundColor: team.color || 'var(--text-primary)',
                  }}
                />
                <EllipsisTooltip
                  text={team.name}
                  className='team-dropdown-select-option-text'
                />
              </button>
            );
          })
        ) : (
          <div className='team-dropdown-select-empty'>
            {searchValue
              ? 'No matches found'
              : `No ${themeT('team', theme.themeType).toLowerCase()}s available`}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`team-dropdown-select ${className}`.trim()}>
      {label && <div className='team-dropdown-select-label'>{label}</div>}
      <Dropdown
        overlay={menu}
        trigger={['click']}
        visible={open}
        onVisibleChange={setDropdownOpen}
        disabled={disabled}
      >
        <div
          ref={triggerRef}
          onClick={toggleOpen}
          tabIndex={disabled ? -1 : 0}
          role='button'
          aria-expanded={open}
          className={`team-dropdown-select-trigger ${disabled ? 'is-disabled' : ''} ${
            open ? 'is-open' : ''
          }`.trim()}
        >
          <div className='team-dropdown-select-values'>
            {selectedTeams.length === 0 ? (
              <span className='team-dropdown-select-placeholder'>
                Select...
              </span>
            ) : (
              selectedTeams.map((team) => (
                <TeamTagPill
                  key={team.id}
                  name={team.name}
                  color={team.color}
                  removable={!disabled}
                  onRemove={(e) => handleRemove(team.id, e)}
                />
              ))
            )}
          </div>
          <div className='team-dropdown-select-actions'>
            {selectedTeams.length > 0 && !disabled && (
              <button
                type='button'
                className='team-dropdown-select-action-button'
                onClick={handleClearAll}
                aria-label='Clear selected teams'
              >
                <CloseCircleFilled className='team-dropdown-select-clear' />
              </button>
            )}
            {!hideChevronWhenDisabled && (
              <span className='team-dropdown-select-action-button'>
                <ChevronDownSmallIcon
                  size={16}
                  color='var(--text-secondary)'
                  className='team-dropdown-select-chevron'
                />
              </span>
            )}
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

export default TeamDropdownSelect;
